import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Form, Input, Spin, Switch } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage } from '@api/server';
import { useAuth } from '@context/auth-context';
import { createUser, getUser, updateUser } from './users';

const User = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser, refreshSession } = useAuth();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const userId = useMemo(() => {
    if (!id) {
      return null;
    }

    const nextId = Number(id);
    return Number.isInteger(nextId) && nextId > 0 ? nextId : null;
  }, [id]);
  const isNewUser = !id;
  const title = isNewUser ? 'Новый пользователь' : `Пользователь #${userId || ''}`;
  const closeDrawer = useCallback(() => {
    navigate('/users');
  }, [navigate]);

  const loadUser = useCallback(async () => {
    if (isNewUser) {
      form.setFieldsValue({
        email: '',
        name: '',
        admin: false,
        active: true,
      });
      return;
    }

    if (!userId) {
      setError('Некорректный идентификатор пользователя.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const user = await getUser(userId);
      form.setFieldsValue({
        email: user.email,
        name: user.name,
        admin: Boolean(user.admin),
        active: Boolean(user.active),
      });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [form, isNewUser, userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const handleSave = async (values) => {
    setError('');
    setIsSaving(true);
    try {
      if (isNewUser) {
        const user = await createUser(values);
        navigate(`/users/${user.id}`);
        return;
      }

      await updateUser({
        id: userId,
        ...values,
      });
      if (currentUser?.id === userId) {
        await refreshSession();
      }
      navigate('/users');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      className="user-drawer user-form-drawer"
      title={title}
      placement="right"
      width={560}
      open
      onClose={closeDrawer}
      destroyOnHidden
      footer={(
        <div className="user-drawer-footer">
          <Button type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={() => form.submit()}>
            Сохранить
          </Button>
          <Button onClick={closeDrawer}>Отмена</Button>
        </div>
      )}
    >
      {error && (
        <Alert className="user-alert" type="error" message={error} showIcon />
      )}

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          initialValues={{
            email: '',
            name: '',
            admin: false,
            active: true,
          }}
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Введите email.' },
              { type: 'email', message: 'Введите корректный email.' },
            ]}
          >
            <Input placeholder="user@example.com" autoFocus />
          </Form.Item>

          <Form.Item
            label="Имя"
            name="name"
            rules={[{ required: true, message: 'Введите имя.' }]}
          >
            <Input placeholder="Имя пользователя" />
          </Form.Item>

          <div className="form-row">
            <Form.Item label="Администратор" name="admin" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label="Активен" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default User;
